import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { isSupabaseConfigured } from './firebase/config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { SearchProvider } from './contexts/SearchContext';
import HomePage from './pages/HomePage';
import AdminPanel from './pages/AdminPanel';
import VendorPanel from './pages/VendorPanel';
import AuthActionPage from './pages/AuthActionPage';
import ComingSoonPage from './pages/ComingSoonPage';
import AboutPage from './pages/AboutPage';

// Protected route component that redirects based on user account type
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated, userAccountType } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated && userAccountType) {
      // Redirect users to their appropriate panel after login/signup
      if (userAccountType === 'admin' && window.location.pathname === '/') {
        navigate('/admin');
      } else if (userAccountType === 'vendor' && window.location.pathname === '/') {
        navigate('/vendor');
      }
    }
  }, [loading, isAuthenticated, userAccountType, navigate]);

  if (loading) {
  return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #FDA949 0%, #FE428E 100%)',
        color: 'white',
        fontSize: '24px',
        fontWeight: '600'
      }}>
        Loading...
            </div>
    );
  }

  return <>{children}</>;
}

// Admin route protection
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated, userAccountType } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #FDA949 0%, #FE428E 100%)',
        color: 'white',
        fontSize: '24px',
        fontWeight: '600'
      }}>
        Loading...
              </div>
    );
  }

  if (!isAuthenticated || userAccountType !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Vendor route protection
function VendorRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated, userAccountType } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #FDA949 0%, #FE428E 100%)',
        color: 'white',
        fontSize: '24px',
        fontWeight: '600'
      }}>
        Loading...
          </div>
    );
  }

  if (!isAuthenticated || userAccountType !== 'vendor') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  return (
    <Routes>
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        } 
      />
      <Route 
        path="/vendor" 
        element={
          <VendorRoute>
            <VendorPanel />
          </VendorRoute>
        } 
      />
      <Route path="/auth/reset-password" element={<AuthActionPage />} />
      <Route path="/auth/confirm-email" element={<AuthActionPage />} />
      <Route path="/coming-soon" element={<ComingSoonPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function MissingSupabaseConfig() {
  return (
    <div
      style={{
        fontFamily: 'system-ui, sans-serif',
        maxWidth: 520,
        margin: '12vh auto',
        padding: 24,
        lineHeight: 1.5,
      }}
    >
      <h1 style={{ fontSize: '1.35rem', marginBottom: 12 }}>Supabase environment missing</h1>
      <p>
        This build was produced without <code>REACT_APP_SUPABASE_URL</code> and{' '}
        <code>REACT_APP_SUPABASE_ANON_KEY</code>. Create React App bakes those in at build time, so they must
        be set on Vercel before the build runs.
      </p>
      <ol style={{ paddingLeft: 20 }}>
        <li>Vercel → your project → Settings → Environment Variables</li>
        <li>Add both variables for Production (and Preview if you use it)</li>
        <li>Redeploy (Deployments → … → Redeploy, or push a new commit)</li>
      </ol>
    </div>
  );
}

function App() {
  if (!isSupabaseConfigured) {
    return <MissingSupabaseConfig />;
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    {/* Keep providers here so auth/cart state is shared across all routes */}
    <AuthProvider>
      <SearchProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </SearchProvider>
    </AuthProvider>
    </Router>
  );
}

export default App;